import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { Entry, Fixture, Round } from '@h2h/shared';
import { Lock } from '../../../../components/ui/icons';
import { SCREEN_COLUMN } from '../../../../components/ui/AppShell';
import { cn } from '../../../../lib/cn';
import { getOverlayRoot, lockAppChrome } from '../../../../lib/overlayRoot';
import { EASE_OUT_IOS, SHEET_DISMISS, SPRING_SHEET, prefersReducedMotion, useIsDesktop } from '../../../../lib/motion';
import { useNow } from '../../helpers';
import {
  CyclingCallsStep,
  CyclingRankFooter,
  CyclingRankStep
} from '../../../make-cycling-picks/CyclingPicksBody';
import { CyclingPicksFlowHeader } from '../../../make-cycling-picks/CyclingPicksFlowHeader';
import { TOTAL } from '../../../make-cycling-picks/constants';
import { useCyclingPicksDraft } from '../../../make-cycling-picks/useCyclingPicksDraft';
import { maybePromptPushPermission } from '../../../../lib/push';
import { toast } from '../../../../lib/toast';
import { useHaptics } from '../../../../hooks/useHaptics';

/**
 * Full-screen cycling pick flow over the league hub — call six duels, rank the
 * ladder, autosave as you go. Same focused surface as CardBuilderSheet: portaled,
 * tab bar hidden, CTAs scroll with content (never sticky).
 */
export function CyclingPicksSheet({
  open,
  onClose,
  leagueId,
  round,
  fixtures,
  entry,
  uid,
  duelFocus
}: {
  open: boolean;
  onClose: () => void;
  leagueId: string;
  round: Round;
  fixtures: Fixture[];
  entry: Entry | null | undefined;
  uid: string | null;
  duelFocus?: string | null;
}) {
  const now = useNow(true);
  const { impact } = useHaptics();
  const reduce = prefersReducedMotion();
  const wide = useIsDesktop();
  const [raised, setRaised] = useState(false);

  const draft = useCyclingPicksDraft({
    leagueId,
    roundId: round.roundId,
    round,
    fixtures,
    entry,
    uid,
    now,
    duelFocus,
    openToStep: 'calls'
  });

  useEffect(() => {
    if (!open) return;
    const unlock = lockAppChrome();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      unlock();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setRaised(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRaised(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  const callsTitle =
    draft.pickedCount === 0
      ? 'Call your duels'
      : remainingLabel(TOTAL - draft.pickedCount);
  const callsBody =
    draft.pickedCount === 0
      ? "Tap who finishes ahead in each duel — you'll rank them by confidence next."
      : 'Edit any call before the stage rolls out. Ranking unlocks once all six are in.';

  async function onDone() {
    if (!draft.valid || !draft.duelPicks) return;
    draft.setError(null);
    impact('medium');
    try {
      await draft.submitEntry.mutateAsync({ leagueId, roundId: round.roundId, duelPicks: draft.duelPicks });
      draft.lastSavedRef.current = JSON.stringify(draft.duelPicks);
      void maybePromptPushPermission();
      toast('Ladder saved — good luck!');
      onClose();
    } catch (err) {
      draft.setError((err as { message?: string })?.message ?? 'Could not save your picks. Please try again.');
    }
  }

  const goBack =
    draft.flowStep === 'rank'
      ? draft.goToCalls
      : onClose;

  if (typeof document === 'undefined') return null;

  // Tablet + desktop: right-docked drawer. Phones: full-screen slide-up.
  return createPortal(
    <AnimatePresence>
      {open && wide && (
        <motion.div
          key="cycling-picks-backdrop"
          aria-hidden
          onClick={onClose}
          className="pointer-events-auto fixed inset-0 bg-night/40 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: raised ? 1 : 0 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: EASE_OUT_IOS } }}
        />
      )}
      {open && (
        <motion.div
          key="cycling-picks-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Call your duels"
          className={cn(
            'pointer-events-auto fixed flex flex-col bg-white',
            wide
              ? 'right-0 top-0 bottom-0 w-[min(400px,46vw)] border-l border-black/10 shadow-[-26px_0_70px_-34px_rgba(20,20,50,0.55)] lg:w-[min(480px,94vw)]'
              : 'inset-0 w-full pt-safe'
          )}
          initial={reduce ? { opacity: 0 } : wide ? { x: '100%' } : { y: '100%' }}
          animate={
            reduce
              ? { opacity: raised ? 1 : 0 }
              : wide
                ? { x: raised ? 0 : '100%' }
                : { y: raised ? 0 : '100%' }
          }
          exit={
            reduce
              ? { opacity: 0 }
              : wide
                ? { x: '100%', transition: SHEET_DISMISS }
                : { y: '100%', transition: SHEET_DISMISS }
          }
          transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_SHEET}
          style={{ willChange: 'transform' }}
        >
          <div className={cn('flex min-h-0 flex-1 flex-col', wide ? 'px-4' : SCREEN_COLUMN)}>
            <div className="native-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto pb-safe">
              {draft.pageLoading ? (
                <div className="flex h-40 items-center justify-center">
                  <span className="h-[9px] w-[9px] animate-h2h-pulse rounded-full bg-accent" />
                </div>
              ) : !draft.slateReady ? (
                <GateCard
                  title="Duels loading"
                  body="The six terrain-matched duels for this stage appear here as soon as they're ready."
                  onClose={onClose}
                />
              ) : draft.frozen ? (
                <GateCard
                  title="Calls closed for this stage"
                  body="The stage has rolled out — your ladder is locked in."
                  onClose={onClose}
                  locked
                />
              ) : draft.flowStep === 'calls' ? (
                <>
                  <CyclingPicksFlowHeader
                    backLabel={draft.stageLabel}
                    step={1}
                    routeLabel={draft.routeLabel}
                    title={callsTitle}
                    body={callsBody}
                    lockAt={draft.lockAt}
                    now={now}
                    progressPct={(draft.pickedCount / TOTAL) * 100}
                    progressLabel={`${draft.pickedCount} / ${TOTAL} called`}
                    progressActive={draft.pickedCount > 0}
                    onBack={goBack}
                  />
                  <CyclingCallsStep
                    slate={draft.slate}
                    sideByDuel={draft.sideByDuel}
                    frozen={draft.frozen}
                    pickedCount={draft.pickedCount}
                    onPick={draft.setSide}
                    onContinue={draft.goToRank}
                    pinBottom={false}
                  />
                </>
              ) : (
                <>
                  <CyclingPicksFlowHeader
                    backLabel="Calls"
                    step={2}
                    routeLabel={draft.routeLabel}
                    title="Rank your calls"
                    body="Drag your surest call to the top — it rides at 6×. Your coin-flip sits at 1×."
                    lockAt={draft.lockAt}
                    now={now}
                    onBack={goBack}
                  />
                  <CyclingRankStep
                    rungs={draft.rungs}
                    frozen={draft.frozen}
                    onReorder={draft.reorderRungs}
                  />
                  <CyclingRankFooter
                    frozen={draft.frozen}
                    valid={draft.valid}
                    error={draft.error}
                    isPending={draft.submitEntry.isPending}
                    onLock={onDone}
                    pinBottom={false}
                  />
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}

function GateCard({
  title,
  body,
  onClose,
  locked = false
}: {
  title: string;
  body: string;
  onClose: () => void;
  locked?: boolean;
}) {
  return (
    <div className="px-safe py-6 md:px-0">
      <div className="rounded-[20px] border border-black/[0.07] bg-white px-[18px] py-6 text-center shadow-[0_18px_40px_-26px_rgba(20,20,50,0.34)]">
        {locked && (
          <div className="mx-auto mb-3 grid h-[42px] w-[42px] place-items-center rounded-[13px] bg-night">
            <Lock width={20} height={20} strokeWidth={2.2} className="text-white" />
          </div>
        )}
        <div className="text-[15px] font-extrabold tracking-snug text-ink">{title}</div>
        <p className="mx-auto mt-1.5 max-w-[280px] text-[12.5px] font-medium leading-[1.45] text-ink2">{body}</p>
        <button
          type="button"
          onClick={onClose}
          className="tap mt-4 w-full rounded-[15px] bg-[#F0F0F4] py-[15px] text-[14px] font-extrabold text-ink active:scale-[0.99]"
        >
          Back to hub
        </button>
      </div>
    </div>
  );
}

function remainingLabel(remaining: number): string {
  if (remaining <= 0) return 'All duels called';
  if (remaining === 1) return '1 duel to call';
  return `${remaining} duels to call`;
}
